/**
 * The morning quick check — `09-equipment/equipment-quick-check.png`.
 *
 * A minute, on an iPad, with gloves on, before the truck leaves. So: one
 * column, big targets, and every row answerable with one thumb. The only
 * typing is the meter, and the keypad it opens is numeric.
 *
 * What it must be honest about is the consequence. Flagging an issue opens a
 * work order and sends it to a named mechanic, and the truck still goes out
 * unless that mechanic says otherwise — so the screen says exactly that,
 * above the button, before anybody presses it.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { Button, Card, EmptyState, SectionLabel, Tag } from '../components/primitives.js'
import { Toast } from '../components/overlays.js'
import { formatNumber } from '../lib/format.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import type { QuickCheckForm } from '../lib/equipment-types.js'

const FUEL = [
  { value: 'empty', label: 'E' },
  { value: 'quarter', label: '¼' },
  { value: 'half', label: '½' },
  { value: 'three_quarter', label: '¾' },
  { value: 'full', label: 'F' },
] as const

type Fuel = (typeof FUEL)[number]['value']

interface Answer {
  ok: boolean | null
  note: string
}

/** One piece of the crew's morning, out of however many they have today. */
interface CrewPiece {
  id: string
  name: string
  assetTag: string
  checkedToday: boolean
}

export function CrewQuickCheck({ me }: { me: MeResponse }) {
  const { equipmentId = '' } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const crewId = params.get('crew')
  const visitId = params.get('visit')

  const back = useCallback(
    () => navigate(crewId ? `/crew?crew=${crewId}` : '/crew'), [navigate, crewId])

  const [form, setForm] = useState<QuickCheckForm | null>(null)
  const [pieces, setPieces] = useState<CrewPiece[]>([])
  const [error, setError] = useState<string | null>(null)
  const [meter, setMeter] = useState('')
  const [fuel, setFuel] = useState<Fuel | null>(null)
  const [answers, setAnswers] = useState<Record<string, Answer>>({})
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [check, day] = await Promise.all([
        request<QuickCheckForm>(`/equipment/${equipmentId}/quick-check`),
        crewId
          ? request<{ equipment: CrewPiece[] }>(`/crew-app/day?crewId=${crewId}`)
          : Promise.resolve({ equipment: [] as CrewPiece[] }),
      ])
      setForm(check)
      setPieces(day.equipment)
      setAnswers(Object.fromEntries(
        check.items.map((item) => [item.id, { ok: null, note: '' }])))
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The check could not be loaded.')
    }
  }, [equipmentId, crewId])

  useEffect(() => { void load() }, [load])

  if (error) {
    return (
      <CheckFrame tenant={me.tenant.name} title="Quick check">
        <EmptyState title="The check could not be loaded" body={error}
          action={{ label: 'Back to the day', onClick: back }} />
      </CheckFrame>
    )
  }
  if (!form) {
    return (
      <CheckFrame tenant={me.tenant.name} title="Quick check">
        <p className="p-6 text-body text-text-muted">Loading…</p>
      </CheckFrame>
    )
  }

  const piece = form.equipment
  const ordered = pieces.length > 0 ? pieces : [{ ...piece, checkedToday: false }]
  const position = ordered.findIndex((row) => row.id === piece.id)
  const issues = Object.values(answers).filter((answer) => answer.ok === false)
  const unanswered = Object.values(answers).filter((answer) => answer.ok === null).length
  const meterWanted = piece.meterKind !== 'none'

  async function submit() {
    if (!form) return
    setBusy(true)
    try {
      const result = await request<{ workOrders: { reference: string }[]; meterWarning: string | null }>(
        `/equipment/${equipmentId}/quick-check`,
        {
          method: 'POST',
          body: {
            visitId,
            crewId,
            meterReading: meter.trim() === '' ? null : Number(meter),
            fuelLevel: fuel,
            results: form.items.map((item) => ({
              itemId: item.id,
              label: item.label,
              ok: answers[item.id]?.ok !== false,
              note: answers[item.id]?.note.trim() || null,
            })),
          },
        })

      // The next piece this crew still owes a check on, if there is one. On an
      // iPad at 6.44 am, "next" is the only navigation anybody wants.
      const next = ordered.find((row) =>
        row.id !== piece.id && !row.checkedToday)

      const raised = result.workOrders.length
      const said = raised === 0
        ? 'Check done.'
        : `Check done. ${formatNumber(raised)} work ${raised === 1 ? 'order' : 'orders'} raised${form.mechanic ? ` for ${form.mechanic.name}` : ''}.`

      if (next) {
        setNotice(`${said} Next: ${next.name}.`)
        navigate(`/crew/check/${next.id}${crewId ? `?crew=${crewId}` : ''}`)
      } else {
        setNotice(result.meterWarning ? `${said} ${result.meterWarning}` : said)
        back()
      }
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That did not save.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <CheckFrame
      tenant={me.tenant.name}
      title={`${piece.name} · quick check`}
      right={ordered.length > 1 && position >= 0
        ? `${position + 1} of ${ordered.length}`
        : undefined}
    >
      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-3 p-5">
        <p className="m-0 text-body text-text-muted">
          {piece.assetTag}
          {' · '}
          {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
            .toLowerCase()}
          {' · about a minute'}
        </p>

        {meterWanted && (
          <Card className="p-4">
            <SectionLabel className="mb-2 block">
              {piece.meterKind === 'miles' ? 'Mileage' : 'Hours'}
            </SectionLabel>
            <input
              inputMode="decimal"
              value={meter}
              onChange={(event) => setMeter(event.currentTarget.value.replace(/[^\d.]/g, ''))}
              placeholder={piece.lastReading === null
                ? '0'
                : String(formatNumber(Number(piece.lastReading)))}
              className="w-full rounded-touch border border-line-strong bg-surface px-4 py-3 text-[28px] font-bold tnum text-text"
            />
            <p className="m-0 mt-2 text-body text-text-muted">
              {piece.lastReading
                ? (
                  <>
                    Last: {formatNumber(Number(piece.lastReading))}
                    {piece.lastReadAt && ` on ${shortDay(piece.lastReadAt)}`}
                    {difference(piece.lastReading, meter)}
                  </>
                )
                : 'No reading on this one yet — this is the first.'}
            </p>
          </Card>
        )}

        <Card className="p-4">
          <SectionLabel className="mb-2 block">Fuel</SectionLabel>
          <div role="radiogroup" aria-label="Fuel level" className="flex gap-2">
            {FUEL.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={fuel === option.value}
                onClick={() => setFuel(option.value)}
                className={cn(
                  'flex-1 rounded-touch border py-3 text-[20px] font-bold',
                  fuel === option.value
                    ? 'border-accent bg-accent text-white'
                    : 'border-line-strong bg-surface text-text',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Card>

        {form.items.length === 0
          ? (
            <Card className="p-4">
              <p className="m-0 text-body text-text-muted">
                Nothing is set up to check on this one. Somebody in the office
                adds the list to its equipment type.
              </p>
            </Card>
          )
          : form.items.map((item) => {
            const answer = answers[item.id] ?? { ok: null, note: '' }
            return (
              <Card key={item.id} className="p-4"
                tone={answer.ok === false ? 'bad' : 'plain'}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="min-w-[180px] flex-1">
                    <span className="block text-[19px] font-semibold text-text">{item.label}</span>
                    {item.hint && (
                      <span className="block text-body text-text-muted">{item.hint}</span>
                    )}
                  </span>

                  <span className="flex gap-2">
                    <Button
                      size="big"
                      variant={answer.ok === true ? 'primary' : 'default'}
                      onClick={() => setAnswers((current) => ({
                        ...current, [item.id]: { ok: true, note: '' },
                      }))}
                    >
                      OK
                    </Button>
                    <Button
                      size="big"
                      variant={answer.ok === false ? 'alert' : 'default'}
                      onClick={() => setAnswers((current) => ({
                        ...current, [item.id]: { ok: false, note: current[item.id]?.note ?? '' },
                      }))}
                    >
                      Issue
                    </Button>
                  </span>
                </div>

                {answer.ok === false && (
                  <textarea
                    value={answer.note}
                    /*
                     * The value is read here and not inside the updater. React
                     * clears `currentTarget` once the handler returns, and an
                     * updater runs later — so reading it in there threw and
                     * took the whole check down on the first keystroke.
                     */
                    onChange={(event) => {
                      const { value } = event.currentTarget
                      setAnswers((current) => ({
                        ...current, [item.id]: { ok: false, note: value },
                      }))
                    }}
                    rows={2}
                    placeholder="What is wrong with it? The mechanic reads this first."
                    className="mt-3 w-full rounded-touch border border-line-strong bg-surface px-3 py-2 text-body text-text"
                  />
                )}
              </Card>
            )
          })}

        {form.pairedWith.length > 0 && (
          <p className="m-0 text-body text-text-muted">
            {form.pairedWith.map((partner) => partner.name).join(', ')}{' '}
            {form.pairedWith.length === 1 ? 'is' : 'are'} paired with this one and
            checked with it.
          </p>
        )}

        {issues.length > 0 && (
          <div className="rounded-card border border-warn bg-warn-bg p-4">
            <p className="m-0 text-body text-text">
              {issues.length === 1 ? 'An issue was flagged.' : `${formatNumber(issues.length)} issues were flagged.`}{' '}
              {form.mechanic
                ? (
                  <>
                    {issues.length === 1 ? 'It goes' : 'They go'} to{' '}
                    <span className="font-bold">{form.mechanic.name}</span> as{' '}
                    {issues.length === 1 ? 'a work order' : 'work orders'} the moment you
                    submit. The truck stays in service unless {form.mechanic.name.split(' ')[0]}{' '}
                    says otherwise.
                  </>
                )
                : (
                  <>
                    Nobody covers this branch for repairs yet, so the work order
                    waits in the shop queue. The truck stays in service.
                  </>
                )}
            </p>
          </div>
        )}

        <div className="sticky bottom-0 -mx-5 flex gap-3 border-t border-line bg-surface p-4">
          <Button size="big" onClick={back}>Not now</Button>
          <Button
            size="big"
            variant="primary"
            className="flex-1"
            disabled={busy || form.items.length === 0}
            onClick={() => void submit()}
          >
            {unanswered > 0
              ? `Submit · ${formatNumber(unanswered)} not answered`
              : 'Submit & clock in'}
          </Button>
        </div>

        {unanswered > 0 && form.items.length > 0 && (
          <p className="m-0 text-body text-text-muted">
            Anything you have not answered is recorded as OK. Tap Issue on
            whatever is not.
          </p>
        )}
      </div>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}
    </CheckFrame>
  )
}

/** The crew frame's bar, without the crew app's day behind it. */
function CheckFrame({
  tenant, title, right, children,
}: {
  tenant: string
  title: string
  right?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex h-app flex-col bg-bg">
      <header className="flex h-[60px] shrink-0 items-center gap-3 border-b border-line bg-surface px-5">
        <span className="text-card font-semibold text-text">{tenant}</span>
        <Tag>Quick check</Tag>
        <span className="text-body text-text">{title}</span>
        {right && (
          <span className="ml-auto">
            <Tag tone="ok">{right}</Tag>
          </span>
        )}
      </header>
      <main className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</main>
    </div>
  )
}

/** "+108 looks right", or nothing at all until there is a number to judge. */
function difference(last: string, typed: string): string {
  if (typed.trim() === '') return ''
  const delta = Number(typed) - Number(last)
  if (Number.isNaN(delta)) return ''
  if (delta < 0) return ` · that is ${formatNumber(Math.abs(delta))} lower — check the number`
  return ` · +${formatNumber(delta)}`
}

const shortDay = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { weekday: 'short' })
